import { Component, DestroyRef, inject, Inject, Input, OnInit } from '@angular/core';
import { ArivoStepperComponent } from '@shared/components/ui-elements';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ContractCreateResolve, ContractLicensePlate, ContractTemplate, Garage, LicensePlate, ProviderAddress, ProviderContactData, ProviderLinkOptions } from '@shared/models';
import { ContractCreateService, ContractTemplateInfo } from '../contract-create.service';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDateWithTimezone, getContractPriceAdjustmentIncreaseString, months, parseDate, setLoading, TypedRoute } from '@shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@shared/services';
import { ContractsApiService } from '../../contracts-api.service';
import { debounceTime, pairwise } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AmplitudeEventService, FeatureFlagService, ProviderSettingsService } from '@core/services';
import { LocalizedCurrencyPipe, LocalizedPercentagePipe } from '@core/pipes';
import {
  ampli,
  ContractCreateCancelled,
  ContractCreateCancelledProperties,
  ContractCreateLicensePlateAdded,
  ContractCreateLicensePlateRemoved,
  ContractCreateSaved,
  ContractCreateSavedProperties,
} from '@integrations/amplitude';

@Component({
  selector: 'app-contract-create-second-step',
  templateUrl: './contract-create-second-step.component.html',
  styleUrls: ['./contract-create-second-step.component.scss'],
})
export class ContractCreateSecondStepComponent implements OnInit {
  @Input() stepperComponent!: ArivoStepperComponent;
  destroyRef = inject(DestroyRef);

  garage?: Garage;
  product?: ContractTemplate;
  licensePlates: ContractLicensePlate[] = [];
  contractTemplateInfo: ContractTemplateInfo | null = null;
  loading = false;
  providerLinks!: ProviderLinkOptions;
  licensePlatesInitialized = false;
  providerContactData: ProviderContactData | null = null;
  uploadedFiles: { file: File; id: string | null; uploading: boolean; error: boolean; errorMsg: string | null }[] = [];
  readonly maxFiles = 5;
  readonly maxFileSizeBytes = 10 * 1024 * 1024; // 10MB
  readonly acceptedFileTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

  contractForm = this._formBuilder.group({
    start: [new Date(), Validators.required],
    parkingSpots: [1, Validators.required],
    licensePlates: [<Array<LicensePlate>>[]],
    termsAndConditions: [false, [Validators.requiredTrue]],
    document_ids: new FormControl<string[] | null>(null),
  });


  constructor(
    private router: Router,
    private _formBuilder: FormBuilder,
    private toastService: ToastService,
    private translateService: TranslateService,
    private contractsApiService: ContractsApiService,
    private contractCreateService: ContractCreateService,
    private providerSettingsService: ProviderSettingsService,
    private localizedCurrencyPipe: LocalizedCurrencyPipe,
    private localizedPercentagePipe: LocalizedPercentagePipe,
    private featureFlagService: FeatureFlagService,
    private amplitudeEventService: AmplitudeEventService,
    @Inject(ActivatedRoute) private route: TypedRoute<ContractCreateResolve>,
  ) {}

  ngOnInit() {
    const licensePlates: ContractLicensePlate[] = this.route.snapshot.data.contractCreateApiResponse.license_plates.map(
      (licensePlate: LicensePlate): ContractLicensePlate => {
        return {
          ...licensePlate,
          enabled: false,
        };
      },
    );

    this.providerSettingsService.providerSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.providerLinks = this.providerSettingsService.getLinks();
    });

    this.providerContactData = this.providerSettingsService.getContactData()


    this.contractCreateService.garageSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((garage) => {
      if (this.garage !== garage) {
        this.garage = garage;

        this.licensePlates = licensePlates.map((licensePlate: LicensePlate): ContractLicensePlate => {
          return {
            ...licensePlate,
            enabled: false,
          };
        });

        this.contractForm.reset();
        this.contractForm.controls.licensePlates.reset();
        this.contractForm.controls.start.patchValue(new Date());
        this.contractForm.controls.parkingSpots.patchValue(1);
        this.contractForm.controls.licensePlates.patchValue([]);
      }
    });

    this.contractCreateService.productSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((product) => {
      this.product = product;
      if (this.product.attachment_field?.mandatory) {
        this.contractForm.controls.document_ids.setValidators([Validators.required]);
      } else {
        this.contractForm.controls.document_ids.clearValidators();
      }
      this.uploadedFiles = [];
      this.contractForm.controls.document_ids.patchValue(null);
      this.contractForm.controls.document_ids.updateValueAndValidity();
      if (this.product.initial_start && parseDate(this.product.initial_start) > new Date()) {
        this.contractForm.controls.start.patchValue(parseDate(this.product.initial_start));
      }
      this.updateContractTemplateInfo();
    });

    this.contractForm.valueChanges
      .pipe(pairwise(), debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe(([oldVal, newVal]) => {
        if (oldVal && this.product && (oldVal.start !== newVal.start || oldVal.parkingSpots !== newVal.parkingSpots)) {
          this.updateContractTemplateInfo();
        }
      });
  }

  get totalPrice(): number {
    return this.contractTemplateInfo?.price.amount ?? 0;
  }

  startDatepickerFilter = (d: Date | null): boolean => {
    if (this.product?.initial_start && parseDate(this.product.initial_start) > new Date()) {
      const date = d || parseDate(this.product.initial_start);
      return parseDate(this.product.initial_start).setHours(0, 0, 0, 0) <= date.valueOf();
    } else {
      const date = d || new Date();
      return new Date().setHours(0, 0, 0, 0) <= date.valueOf();
    }
  };

  updateLicensePlates(licensePlates: LicensePlate[]) {
    if (!this.licensePlatesInitialized) {
      this.licensePlatesInitialized = true;
    } else {
      this.trackLicensePlateChangesToAmplitude(this.contractForm.controls.licensePlates.value ?? [], licensePlates);
    }
    this.contractForm.controls.licensePlates.patchValue(licensePlates);
    this.contractForm.controls.licensePlates.updateValueAndValidity();
  }

  updateContractTemplateInfo() {
    if (!this.product) {
      return;
    }
    this.contractCreateService
      .getContractTemplateInfo(
        this.product.id,
        this.contractForm.value.parkingSpots!,
        formatDateWithTimezone(this.contractForm.value.start!),
      )
      .pipe(setLoading(this))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((contractTemplateInfo) => {
        this.contractTemplateInfo = contractTemplateInfo;
      });
  }

  get isFirstOfMonth(): boolean {
    const startDate = this.contractForm.get('start')?.value;
    return startDate ? startDate.getDate() === 1 : false;
  }

  get proratedPrice(): number {
    return this.contractTemplateInfo?.first_payment_period.price.amount ?? 0;
  }

  get increasedPrice(): number {
    return this.contractTemplateInfo?.increased_price?.amount ?? 0;
  }

  get vatPercent(): number {
    return this.contractTemplateInfo?.vat_percent ?? 0;
  }

  get confirmationButtonText(): string {
    if (this.approvalRequired) {
      return 'Contracts.RequestWithCosts';
    }
    return 'Contracts.ConcludeContractWithCosts';
  }

  get documentsTitleText(): string {
    if (this.product?.attachment_field?.mandatory) {
      return 'Contracts.RequiredDocumentsTitle';
    }
    return 'Contracts.OptionalDocumentsTitle';
  }

  get garageBillingAddress(): string {
    const address: ProviderAddress | undefined = this.garage?.billing_address;
    if (!address) {
      return '';
    }

    let address_line: string = `${address.name}, ${this.translateService.instant('Contracts.GarageBillingAddressBasedIn')} ${address.address1}`;
    if (address.address2) {
      address_line += `, ${address.address2}`;
    }
    address_line += `, ${address.zip_code} ${address.city}, ${address.country.name}`;
    return address_line;
  }

  get approvalRequired(): boolean {
    if (!this.product) {
      return false;
    }
    return this.product.bookable === 'public' && this.product.approval_required;
  }

  get canAddMoreFiles(): boolean {
    return this.uploadedFiles.length < this.maxFiles;
  }

  get isUploading(): boolean {
    return this.uploadedFiles.some((e) => e.uploading);
  }

  get showVpi(): boolean {
    return this.featureFlagService.getFeatureFlag('enable_wipark_vpi');
  }

  back() {
    let ampliEvent: ContractCreateCancelledProperties['contractType'] = this.approvalRequired ? 'request' : 'contract'
    this.amplitudeEventService.trackEvent(new ContractCreateCancelled({ contractType: ampliEvent }));
    this.stepperComponent.previous();
  }


  finish() {
    this.contractForm.markAllAsTouched();
    this.contractForm.controls.licensePlates.updateValueAndValidity();

    if (this.isUploading) {
      this.toastService.add({
        message: this.translateService.instant('Contracts.DocumentUploadInProgress'),
        type: 'error',
      });
      return;
    }

    const licensePlates = this.contractForm.value.licensePlates;

    if (this.contractForm.valid && this.garage && this.product) {
      const contract = {
        contract_template_id: this.product.id,
        group_limit: this.contractForm.value.parkingSpots!,
        document_ids: this.contractForm.value.document_ids ?? [],
        license_plates: licensePlates!,
        start: formatDateWithTimezone(this.contractForm.value.start!),
        end: null,
      };

      let successCreateMessage = this.approvalRequired ? "Contracts.CreateContractRequestSuccess" : "Contracts.CreateContractSuccess";
      let ampliEvent: ContractCreateSavedProperties['contractType'] = this.approvalRequired ? 'request' : 'contract';

      this.contractsApiService
        .createContract(contract)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.amplitudeEventService.trackEvent(new ContractCreateSaved({ contractType: ampliEvent }));
            this.router.navigateByUrl('/dashboard').finally(() => {
              this.toastService.add({
                message: this.translateService.instant(successCreateMessage),
                type: 'success',
              });
            });
          },
          error: (error) => {
            if (error.status === 400 && error.error.detail) {
              const detail = error.error.detail;
              for (const key in detail) {
                if (detail.hasOwnProperty(key)) {
                  detail[key].forEach((message: string) => {
                    this.toastService.add({
                      message: message,
                      type: 'error',
                    });
                  });
                }
              }
            } else {
              this.toastService.add({
                message: this.translateService.instant('Contracts.CreateContractError'),
                type: 'error',
              });
            }
          },
        });
    }
  }

  getPriceIncreaseString(contractTemplate: ContractTemplate): string | null {
    return getContractPriceAdjustmentIncreaseString(
      contractTemplate,
      'Contracts.PriceIncreaseInfoCreate',
      this.translateService,
      this.localizedCurrencyPipe,
      this.localizedPercentagePipe,
    );
  }

  getIncreasedPriceString(contractTemplate: ContractTemplate): string | null {
    let translationPlurality;
    if (!contractTemplate.price_adjustment) {
      return null;
    }
    translationPlurality = contractTemplate.price_adjustment.month_start === contractTemplate.price_adjustment.month_end ? 'Singular' : 'Plural';
    return this.translateService.instant(`Contracts.IncreasedPriceInfoCreate.${translationPlurality}`, {
      start: this.translateService.instant('Common.MonthNames.' + months[contractTemplate.price_adjustment.month_start - 1]),
      end: this.translateService.instant('Common.MonthNames.' + months[contractTemplate.price_adjustment.month_end - 1]),
    });
  }

  trackLicensePlateChangesToAmplitude(previousLicensePlates: LicensePlate[], licensePlates: LicensePlate[]): void {
    let previousEnabledLicensePlates = previousLicensePlates.length;
    let enabledLicensePlates = licensePlates.length;
    let maxEnabledLicensePlates = this.contractForm.value.parkingSpots ?? 0;

    const payload = this.amplitudeEventService.buildContractParkingSpacesPayload(maxEnabledLicensePlates, enabledLicensePlates);

    if (previousEnabledLicensePlates < enabledLicensePlates) {
      this.amplitudeEventService.trackEvent(new ContractCreateLicensePlateAdded(payload));
    } else if (previousEnabledLicensePlates > enabledLicensePlates) {
      this.amplitudeEventService.trackEvent(new ContractCreateLicensePlateRemoved(payload));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const newFiles = Array.from(input.files);
      const available = this.maxFiles - this.uploadedFiles.length;
      newFiles.slice(0, available).forEach((file) => {
        const validation: boolean = this.validateFile(file);
        if (!validation) {
          return;
        }
        const entry = { file, id: null as string | null, uploading: true, error: false, errorMsg: '' };
        this.uploadedFiles = [...this.uploadedFiles, entry];
        this.uploadFile(entry);
      });
      input.value = '';
    }
  }

  removeFile(index: number): void {
    this.uploadedFiles = this.uploadedFiles.filter((_, i) => i !== index);
    this.updateDocumentIdsControl();
  }

  onFileReplace(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const validation: boolean = this.validateFile(file);
      if (!validation) {
        input.value = '';
        return;
      }
      const entry = { file, id: null as string | null, uploading: true, error: false, errorMsg: null as string | null };
      this.uploadedFiles[index] = entry;
      this.updateDocumentIdsControl();
      this.uploadFile(entry);
      input.value = '';
    }
  }

  private validateFile(file: File): boolean {
    let errorKey: string | null = null;
    if (!this.acceptedFileTypes.includes(file.type)) {
      errorKey = 'Contracts.DocumentFileTypeNotSupported';
    }

    if (file.size > this.maxFileSizeBytes) {
      errorKey = 'Contracts.DocumentFileTooLarge';
    }
    if (errorKey) {
      this.toastService.add({
        message: this.translateService.instant(errorKey),
        type: 'error',
      });
      return false;
    }
    return true;
  }

  retryFile(index: number): void {
    const entry = this.uploadedFiles[index];
    entry.uploading = true;
    entry.error = false;
    entry.errorMsg = null;
    this.uploadFile(entry);
  }

  private uploadFile(entry: { file: File; id: string | null; uploading: boolean; error: boolean; errorMsg: string | null }): void {
    this.contractsApiService
      .uploadDocument(entry.file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          entry.id = resp.id;
          entry.uploading = false;
          entry.error = false;
          entry.errorMsg = null;
          this.updateDocumentIdsControl();
        },
        error: (err) => {
          entry.uploading = false;
          entry.error = true;
          entry.errorMsg = this.translateService.instant('Contracts.DocumentUploadError');
          if (err.status === 400 && err.error?.detail?.file?.length) {
              const detail = err.error.detail;
              for (const key in detail) {
                if (detail.hasOwnProperty(key)) {
                  detail[key].forEach((message: string) => {
                    this.toastService.add({
                      message: message,
                      type: 'error',
                    });
                  });
                }
              }
            entry.errorMsg = err.error.detail?.file.length ? err.error.detail.file[0] : this.translateService.instant('Contracts.DocumentUploadError');
          } else {
            this.toastService.add({
              message: this.translateService.instant('Contracts.DocumentUploadError'),
              type: 'error',
            });
          }
        },
      });
  }

  private updateDocumentIdsControl(): void {
    const ids = this.uploadedFiles.map((e) => e.id).filter((id): id is string => !!id);
    this.contractForm.controls.document_ids.setValue(ids.length > 0 ? ids : null);
  }
}
